import {Injectable} from '@angular/core';

import {BehaviorSubject, EMPTY, Observable} from 'rxjs';
import {catchError, filter, map, tap} from 'rxjs/operators';

import * as moment from 'moment/moment';

import {ClaimType, OngoingPayPeriod} from './claims/claims.interfaces';

import {ToastrService} from 'ngx-toastr';
import {ProfileMode, REQUEST_OPTIONS} from '../app.constants';
import {LeaveAnswer, LeaveAnswerWorkflowV2} from '../inbox/shared/communications.interface';
import {ApiService} from '../shared/api';
import {AuthenticationEvent, AuthenticationService} from '../shared/authentication';
import {Contract, Employee, EmployeeDetail} from '../shared/employees/employees.model';
import {ZoneValues} from '../shared/form-generator/form-generator.interfaces';
import {User, UserService} from '../shared/user';
import {isPresent} from '../shared/utils/lang';
import {ProcessInstance} from '../shared/workflow/workflow-v2.interface';
import {ESS_CLAIM_WORKFLOW, getWorkflowKey, WorkflowV2Service} from '../shared/workflow/workflow-v2.service';

export interface LeaveRequestData {
  isHr?: boolean;
  empAbsenceId?: number;
  pendingEntityId?: number;
  cliEmpId: number;
  contractId: number;
  firstName: string;
  lastName: string;
  name: string;
  beginDate: string;
  beginDateFactor: number;
  endDate: string;
  endDateFactor: number;
  comment: string;
  transId: string;
  limitValue?: number;
  values?: any;
}

export interface ClaimData {
  id: number;
  beginDate: string;
  endDate: string;
  begindate?: string;
  enddate?: string;
  name: string;
  value: number;
  cliCoreID: number;
  unitTransId: string;
  transId: string;
  empContract: number;
  evType: string;
  comment: string;
  readonly: boolean;
  entityWithPaySerie: string;
  values: any;
  empVariableId: number;
  status: string;
  workflowStatus: string;
  creationDate: string;
  payPeriodTransId: string;
  currencyString?: string;
  contractId?: number;
}

export interface ContractInfo extends AllEmpContractsMetaData {
  contractNumber: string;
  cliRegId: number;
}

export interface AllEmpContractsMetaData {
  id: number;
  cliRegNickName: string;
  beginDate: string;
  endDate: string;
}

interface PositionInfo {
  id: number;
  name: string;
}

interface BackupEmployeeInfo {
  id: number;
  firstName: string;
  lastName: string;
  intFirstName: string;
  intLastName: string;
  photoUuid: string;
  employeeNumber: string;
}

export interface EmployeeBackup {
  backupId: number;
  backupBeginDate: string;
  backupEndDate: string;
  contract: ContractInfo;
  position: PositionInfo;
  backupEmployee: BackupEmployeeInfo;
}

export interface CandidateBackup {
  contractId: string;
  backupEmployeeId: string;
  backupBeginDate: string;
  backupEndDate: string;
}

@Injectable()
export class ESSService {
  public employee: Employee;
  public selectedContract: Contract;

  public userSubject: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  public currentUser = this.userSubject.asObservable(); // .distinctUntilChanged();

  public employeeSubject: BehaviorSubject<Employee> = new BehaviorSubject<Employee>(null);
  public currentEmployee = this.employeeSubject.asObservable();

  public contractSubject: BehaviorSubject<Contract> = new BehaviorSubject<Contract>(null);
  public currentContract = this.contractSubject.asObservable().pipe(filter((c: Contract) => c !== null));

  private user: User;

  constructor(private api: ApiService,
              private userService: UserService,
              private toastrService: ToastrService,
              private authenticationService: AuthenticationService,
              private workflowV2Service: WorkflowV2Service) {
    this.initUser();

    this.authenticationService.authenticationEvents.subscribe(result => {
      if (result.event === AuthenticationEvent.LOGOUT) {
        this.user = null;
        this.employee = null;
        this.selectedContract = null;
        this.userSubject.next(this.user);
        this.contractSubject.next(this.selectedContract);
      }
    });
  }

  public selectContract(id?: number) {
    let contract: Contract;
    if (id) {
      contract = this.employee.contracts.find((c: Contract) => c.id === id);
    }

    if (!contract) {
      contract = this.getMainContract(this.employee.contracts);
    }

    if (this.selectedContract !== contract) {
      this.selectedContract = contract;

      this.contractSubject.next(contract);
    }
  }

  public sendLeaveAnswer(values: LeaveAnswer) {
    return this.api.post(`/ga/api/v1/workflow/processReply/${this.user.cliEmpId}`, values);
  }

  public sendLeaveAnswerWorkflowV2(taskId: string, taskDefinitionKey: string, variables: LeaveAnswerWorkflowV2,
                                   notificationId: number, reply: string) {
    return this.workflowV2Service.completeTaskAndNotify(taskId, taskDefinitionKey, variables, notificationId, reply);
  }

  public updatePersonalInfo(values: { [key: string]: ZoneValues[] }) {
    return this.api.post(`/ga/api/v1/workflow/changeInfo/${this.selectedContract.id}`, values);
  }

  public getEmployee(id: number): Observable<any> {

    if (!isPresent(id)) {
      return EMPTY;
    }

    return this.api.get(`/ga/api/v1/cliEmp?search=id==${id}&isEss=true`)
      .pipe(
        tap(result => {
          const data = <EmployeeDetail>result.data[0];
          if (data) {
            this.employee = new Employee(data);
            this.employeeSubject.next(this.employee);
            this.selectContract();

            // moment.tz.setDefault(this.user.timezone || getDefaultLocalTimezone());
            this.userSubject.next(this.user);
          }
        }),
        catchError(() => EMPTY)
      );
  }

  public getClaims(page = 0, perPage: number): Observable<{ total: number, data: ClaimData[] }> {
    return this.api
      .get(`/variable/api/v1/empVariable/getPendingVariables/${this.selectedContract.id}?perPage=${perPage}&page=${page}`,
        REQUEST_OPTIONS.OBSERVE.RESPONSE
      )
      .pipe(
        map(response => ({
          total: response.headers.get('x-total-count') || 0,
          data: response.body
        }))
      );
  }

  public getClaimTypes(cliRegId: number): Observable<ClaimType[]> {
    return this.api.config.get(`/mySpace/${cliRegId}/variableType`)
      .pipe(map(result => result.data));
  }

  public getOngoingAccus(search?: string): Observable<any> {
    return this.api.payroll.get(`/empContract/${this.selectedContract.id}/ongoingAccus?search=&${search}`);
  }

  public createClaim(empVariable: ClaimData) {
    return this.api.ga.post(`/workflow/askVariable`, empVariable);
  }

  public createClaimWorkflowV2(cliRegId: number, empVariable: ClaimData): Observable<ProcessInstance> {
    const workflowKey = getWorkflowKey(ESS_CLAIM_WORKFLOW, cliRegId);
    return this.workflowV2Service.startProcessInstance(workflowKey, {
      request: empVariable,
      claimName: empVariable.name,
      cliRegId
    },
    false,
    ProfileMode.ESS);
  }

  public getOnGoingPayPeriod(profileMode: ProfileMode): Observable<OngoingPayPeriod> {
    return this.api.get(`/payroll/api/v1/payPeriod/ongoingByContract/${this.selectedContract.id}?profile-mode=${profileMode}`);
  }

  private initUser() {
    this.userService.getUser().subscribe((user: User) => {
      this.user = user;
      this.getEmployee(this.user.cliEmpId).subscribe();
    });
  }

  private getMainContract(contracts: Contract[]): Contract {
    const now: Date = moment().startOf('day').toDate();

    return contracts.find((contract) => contract.mainContract)
      || contracts.find((contract) => moment(contract.enddate).isAfter(now))
      || contracts[0];
  }

}
